/**
 * «Вода» на каноне — `/water`, когда маршрут объявлен источником `api`.
 *
 * Концепция K2: полив №N — работа агроцикла культуры. Экран выстроен
 * master-detail: слева лента поливов выбранного цикла (свежее сверху), справа
 * выбранный полив — его числа, карта полива по распределениям подачи на
 * контуры и хозяйства под картой. Ниже — каналы и разрыв по хозяйствам, в
 * раскрытии — сверка и журнал заявок.
 *
 * Данные: поливы — работы `irrigation` из картины сезона (`season/picture`,
 * там же оси отчётов); вода полива — `/water/works/{id}` (баланс, подачи,
 * распределения по контурам). Ось цикла сервер к водному балансу не
 * применяет (у заявки нет культуры), поэтому вода цикла собирается из его
 * поливов. Оговорки о происхождении данных печатаются словами сервера.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Droplets, FileMinus, FileSearch, Gauge, Layers, Map as MapIcon, Scale, Timer } from 'lucide-react';
import { api, type QueryParams, type WaterDistrict, type WaterWork } from '../../api/client';
import { useRequest, type RequestResult } from '../../api/useRequest';
import { useAnalysisContext } from '../../analysis/context';
import { PageHeader } from '../ui/PageHeader';
import { Card, Grid, Stack } from '../ui/Card';
import { KpiCard } from '../ui/KpiCard';
import { DataTable, type Column } from '../ui/DataTable';
import { DoneSplit } from '../ui/DoneSplit';
import { Badge } from '../ui/Badge';
import { Disclosure } from '../ui/Disclosure';
import { EmptyState } from '../ui/EmptyState';
import { LoadError } from '../ui/LoadError';
import { Skeleton, SkeletonCard, SkeletonChart, SkeletonKpi, SkeletonList, SkeletonMap, SkeletonRegion, SkeletonTable, SkeletonText } from '../ui';
import { EvidenceMark } from '../ui/EvidenceMark';
import { InfoPopover } from '../../components/InfoPopover';
import { Icon } from '../ui/Icon';
import { ChoiceSelect } from '../ui/AnalysisControls';
import { AgroMap, type MapPanel } from '../map/MapWidget';
import { useAsOfTimeline } from '../map/useAsOfTimeline';
import { usePageTrail } from '../shell/ShellContext';
import { countOf, dateShort, haUnit, m3, m3Compact, num, pct, plural, windowLabel, WORDS } from '../lib/format';
import { defineMessages, type Message } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { tr } from '../../i18n/runtime';
import {
  cycleChoices,
  irrigationWorks,
  pickCycle,
  pickWork,
  toCycleWater,
  toLedger,
  toWaterWork,
  wateringState,
  workKey,
  type ChannelRow,
  type CycleChoice,
  type CycleWaterView,
  type DeliveryView,
  type IrrigationWork,
  type LedgerRow,
  type WaterWorkView,
  type WateringState,
} from '../data-source/api/water';
import {
  cycleWaterShare,
  waterShareMethod,
  waterShareWords,
  wateringTitle,
  type CycleWaterShare,
  type FarmWaterRow,
  type FarmWaterState,
} from './waterSummary';
import { ChannelChip, ChannelList, CoverageStrip, FarmDiverging, type CoveragePoint } from './WaterCharts';
import {
  districtMetric,
  districtReferenceSummary,
  useWaterDistrict,
  WaterDistrictReference,
  WaterWhereToLook,
} from './water/WaterDistrictFile';
import './pages.css';
import './water.css';

const LEDGER_PAGE = 25;
const MAP_HEIGHT = 500;
/** Предел очереди разбора на сервере (`/water/review`): при нём число — «не меньше». */
const REVIEW_CAP = 100;

const m = defineMessages({
  // --- слова с числом (ICU: русские формы; # — число)
  requests: {
    id: 'water.canon.count.requests',
    defaultMessage: '{count, plural, one {# заявка} few {# заявки} many {# заявок} other {# заявки}}',
    description: 'Число заявок хозяйств на воду; подпись под значением «Запрошено»',
  },
  deliveries: {
    id: 'water.canon.count.deliveries',
    defaultMessage: '{count, plural, one {# подача} few {# подачи} many {# подач} other {# подачи}}',
    description: 'Число подач воды (подача — открытие канала); # — число',
  },
  days: {
    id: 'water.canon.count.days',
    defaultMessage: '{count, plural, one {# день} few {# дня} many {# дней} other {# дня}}',
    description: 'Число дней до конца окна полива; # — число',
  },
  dayWord: {
    id: 'water.canon.count.dayWord',
    defaultMessage: '{count, plural, one {день} few {дня} many {дней} other {дня}}',
    description: 'Единица «день» после числа в KPI «Идёт полив» (только слово, число стоит отдельно крупным шрифтом)',
  },
  waterings: {
    id: 'water.canon.count.waterings',
    defaultMessage: '{count, plural, one {# полив} few {# полива} many {# поливов} other {# полива}}',
    description: 'Число поливов агроцикла (бейдж в шапке карточки «Поливы агроцикла»)',
  },
  wateringWorksDenominator: {
    id: 'water.canon.denominator.wateringWorks',
    defaultMessage:
      '{count, plural, one {работа на поле полива} few {работы на полях полива} many {работ на полях полива} other {работы на полях полива}}',
    description: 'Единица знаменателя под полосой выполнения полива («12 из 40 работ на полях полива»); только слово, без числа',
  },
  farmsDenominator: {
    id: 'water.canon.denominator.farms',
    defaultMessage: '{count, plural, one {хозяйство} few {хозяйства} many {хозяйств} other {хозяйства}}',
    description: 'Единица знаменателя под таблицей хозяйств полива («показано 8 из 20 хозяйств»); только слово, без числа',
  },
  deliveriesDenominator: {
    id: 'water.canon.denominator.deliveries',
    defaultMessage: '{count, plural, one {подача} few {подачи} many {подач} other {подачи}}',
    description: 'Единица знаменателя под таблицей расхождений сверки; только слово, без числа',
  },
  requestsDenominator: {
    id: 'water.canon.denominator.requests',
    defaultMessage: '{count, plural, one {заявка} few {заявки} many {заявок} other {заявки}}',
    description: 'Единица знаменателя под журналом заявок на воду; только слово, без числа',
  },
  mismatchesAndUnplanned: {
    id: 'water.canon.reconcile.summary',
    defaultMessage:
      '{mismatched, plural, one {# расхождение сверки} few {# расхождения сверки} many {# расхождений сверки} other {# расхождения сверки}} · вне плана полива: {unplanned}',
    description: 'Сводка свёрнутого блока «Сверка и журнал заявок»; {mismatched} — число расхождений сверки (число), {unplanned} — число подач вне плана полива (уже отформатировано)',
  },
  // --- статусы
  statusPlanned: { id: 'water.canon.status.planned', defaultMessage: 'запланирован', description: 'Статус полива в подписи выбора полива: запланирован; строчными, коротко' },
  statusAnnounced: { id: 'water.canon.status.announced', defaultMessage: 'объявлен', description: 'Статус полива в подписи выбора полива: окно объявлено; строчными, коротко' },
  statusActive: { id: 'water.canon.status.active', defaultMessage: 'идёт', description: 'Статус полива в подписи выбора полива: окно идёт; строчными, коротко' },
  statusPaused: { id: 'water.canon.status.paused', defaultMessage: 'приостановлен', description: 'Статус полива в подписи выбора полива: приостановлен; строчными, коротко' },
  statusCompleted: { id: 'water.canon.status.completed', defaultMessage: 'завершён', description: 'Статус полива в подписи выбора полива: завершён; строчными, коротко' },
  statusCancelled: { id: 'water.canon.status.cancelled', defaultMessage: 'отменён', description: 'Статус полива в подписи выбора полива: отменён; строчными, коротко' },
  reconMatched: { id: 'water.canon.recon.matched', defaultMessage: 'сошлось', description: 'Итог сверки подачи: сумма распределений равна итогу; строчными, коротко' },
  reconMismatched: { id: 'water.canon.recon.mismatched', defaultMessage: 'расходится', description: 'Итог сверки подачи в колонке «Сверка»: сумма распределений не равна итогу; строчными, коротко' },
  reconUnverifiable: { id: 'water.canon.recon.unverifiable', defaultMessage: 'сверить нечем', description: 'Итог сверки подачи: сверять не с чем; строчными, коротко' },
  noData: { id: 'water.canon.noData', defaultMessage: 'нет данных', description: 'Вместо числа или объёма воды, когда источник его не назвал: не ноль, а отсутствие данных' },
  // --- выбор агроцикла и полива
  cycleLabel: { id: 'water.canon.picker.cycle', defaultMessage: 'Агроцикл', description: 'Подпись поля выбора агроцикла (культура × сезон) на экране «Вода»' },
  currentCycle: { id: 'water.canon.picker.cycleCurrent', defaultMessage: 'текущий', description: 'Пояснение под названием агроцикла в списке выбора: это текущий агроцикл; строчными, коротко' },
  wateringLabel: { id: 'water.canon.picker.watering', defaultMessage: 'Полив', description: 'Подпись поля выбора полива (№1, №2…) внутри агроцикла' },
  pickerOption: { id: 'water.canon.picker.option', defaultMessage: '{window} · {status}', description: 'Пояснение под названием полива в списке выбора; {window} — окно полива («01–06 окт»), {status} — статус словом (идёт, завершён…)' },
  massifLabel: { id: 'water.canon.filters.massif', defaultMessage: 'Массив', description: 'Подпись поля выбора массива (группа хозяйств района) на экране «Вода»' },
  allMassifs: { id: 'water.canon.filters.allMassifs', defaultMessage: 'все массивы', description: 'Пункт списка выбора массива: без фильтра, все массивы района; строчными' },
  // --- бейджи полива
  badgeActiveLeft: { id: 'water.canon.badge.activeLeft', defaultMessage: 'идёт, осталось {days}', description: 'Бейдж полива: окно идёт; {days} — число дней до конца окна словом («3 дня»); строчными, коротко' },
  badgeActive: { id: 'water.canon.badge.active', defaultMessage: 'идёт', description: 'Бейдж полива: окно идёт; строчными, коротко' },
  badgeDone: { id: 'water.canon.badge.done', defaultMessage: 'окно закрыто', description: 'Бейдж полива: окно закрылось; строчными, коротко' },
  badgeUpcoming: { id: 'water.canon.badge.upcoming', defaultMessage: 'впереди', description: 'Бейдж полива: окно впереди (ещё не открылось); строчными, коротко' },
  badgeNoWindow: { id: 'water.canon.badge.noWindow', defaultMessage: 'окно не назначено', description: 'Бейдж полива: у полива нет окна в плане; строчными, коротко' },
  farmDelivered: { id: 'water.canon.farmState.delivered', defaultMessage: 'подано', description: 'Состояние воды хозяйства в колонке «Покрытие»: подано всё запрошенное; строчными, коротко' },
  farmPartial: { id: 'water.canon.farmState.partial', defaultMessage: 'частично', description: 'Состояние воды хозяйства в колонке «Покрытие»: подана часть; строчными, коротко' },
  farmNone: { id: 'water.canon.farmState.none', defaultMessage: 'не подано', description: 'Состояние воды хозяйства в колонке «Покрытие»: ничего не подано; строчными, коротко' },
  farmUnmeasured: { id: 'water.canon.farmState.unmeasured', defaultMessage: 'не измерено', description: 'Состояние воды хозяйства в колонке «Покрытие»: объём не измерен — пробел учёта; строчными, коротко' },
  // --- KPI
  kpiRunning: { id: 'water.canon.kpi.running', defaultMessage: 'Идёт {watering}', description: 'Подпись KPI, когда окно полива идёт; {watering} — название полива строчными («полив №5»)' },
  kpiLast: { id: 'water.canon.kpi.last', defaultMessage: 'Последний полив', description: 'Подпись KPI, когда окно не идёт, но есть закрытый полив' },
  kpiIdle: { id: 'water.canon.kpi.idle', defaultMessage: 'Полив сейчас не идёт', description: 'Подпись KPI, когда нет ни идущего, ни закрытого полива' },
  kpiNone: { id: 'water.canon.kpi.none', defaultMessage: 'нет', description: 'Значение KPI, когда поливов с окном нет; одно слово' },
  kpiRunningNote: { id: 'water.canon.kpi.runningNote', defaultMessage: 'до конца окна {date}', description: 'Подпись под KPI «Идёт полив»; {date} — дата закрытия окна' },
  kpiLastNote: { id: 'water.canon.kpi.lastNote', defaultMessage: 'окно закрыто {date} · сейчас полив не идёт', description: 'Подпись под KPI «Последний полив»; {date} — дата закрытия окна' },
  kpiNoWindows: { id: 'water.canon.kpi.noWindows', defaultMessage: 'поливов с окном в агроцикле нет', description: 'Подпись под KPI, когда у агроцикла нет поливов с окном' },
  kpiWater: { id: 'water.canon.kpi.water', defaultMessage: 'Вода агроцикла', description: 'Подпись KPI: доля воды агроцикла — подано от запрошенного, % ' },
  kpiNoRequests: { id: 'water.canon.kpi.noRequests', defaultMessage: 'нет заявок', description: 'Значение KPI «Вода агроцикла», когда заявок на воду нет и делить не на что' },
  kpiWaterNote: {
    id: 'water.canon.kpi.waterNote',
    defaultMessage: '{words} · {state, select, unknown {недоподача —} some {недоподача {value}} other {недоподачи нет}} · {cycle}',
    description: 'Подпись под KPI «Вода агроцикла»; {words} — «подано от запрошенного», {state} — unknown/some/none (недоподача неизвестна/есть/нет), {value} — объём недоподачи (м³), {cycle} — название агроцикла',
  },
  kpiReading: { id: 'water.canon.kpi.reading', defaultMessage: 'читаем воду агроцикла', description: 'Подпись под KPI «Вода агроцикла», пока данные читаются' },
  kpiSilentRunning: { id: 'water.canon.kpi.silentRunning', defaultMessage: 'Без отчёта в окне', description: 'Подпись KPI: сколько работ на полях полива остались без отчёта хозяйства, пока окно идёт' },
  kpiSilent: { id: 'water.canon.kpi.silent', defaultMessage: 'Без отчёта по поливу', description: 'Подпись KPI: сколько работ на полях последнего полива остались без отчёта хозяйства' },
  kpiOf: { id: 'water.canon.kpi.of', defaultMessage: 'из {works}', description: 'Единица рядом со значением KPI: общее число; {works} — число словом («40 работ на полях»)' },
  kpiSilentRunningNote: { id: 'water.canon.kpi.silentRunningNote', defaultMessage: 'окно идёт: пустой отчёт пока значит «ещё не срок»', description: 'Подпись под KPI «Без отчёта в окне»: пока окно идёт, отсутствие отчёта — не просрочка' },
  kpiSilentNote: { id: 'water.canon.kpi.silentNote', defaultMessage: '{watering}: отчёта о поливе не поступало', description: 'Подпись под KPI «Без отчёта по поливу»; {watering} — название полива («Полив №5»)' },
  kpiMismatch: { id: 'water.canon.kpi.mismatch', defaultMessage: 'Расхождения сверки', description: 'Подпись KPI: сколько подач агроцикла не сошлись при сверке' },
  kpiMismatchNote: { id: 'water.canon.kpi.mismatchNote', defaultMessage: 'подачи агроцикла, где сумма распределений не равна итогу', description: 'Подпись под KPI «Расхождения сверки»' },
  // --- вода по событиям файла (заявок нет)
  kpiDelivered: { id: 'water.canon.kpi.delivered', defaultMessage: 'Подано агроцикла', description: 'Подпись KPI: объём воды (м³), поданной за агроцикл по событиям файла ирригации (заявок нет)' },
  kpiDeliveredNote: { id: 'water.canon.kpi.deliveredNote', defaultMessage: 'по файлу ирригации · на гектар политой земли: {value}', description: 'Подпись под KPI «Подано агроцикла»; {value} — м³ на гектар политой земли с единицей или «нет данных»' },
  kpiDoneLand: { id: 'water.canon.kpi.doneLand', defaultMessage: 'Полито по земле', description: 'Подпись KPI: доля политой земли агроцикла в % площади пар «контур × полив» с наступившим окном' },
  kpiDoneLandNote: { id: 'water.canon.kpi.doneLandLandNote', defaultMessage: 'полито {land} · по парам «контур × полив»: {pairs}', description: 'Подпись под KPI «Полито по земле»; {land} — политая земля (га, каждый гектар один раз), {pairs} — та же доля по числу пар (%)' },
  kpiNoDelivery: { id: 'water.canon.kpi.noDelivery', defaultMessage: 'Без подачи', description: 'Подпись KPI: земля (га) поливов, окно которых закрылось без события в файле ирригации' },
  kpiNoDeliveryNote: { id: 'water.canon.kpi.noDeliveryNote', defaultMessage: '{pairs} · ждём подачи: {waiting}', description: 'Подпись под KPI «Без подачи»; {pairs} — число пар «контур × полив» без подачи («1 892 пары»), {waiting} — земля, где окно ещё идёт (га)' },
  kpiReview: { id: 'water.canon.kpi.review', defaultMessage: 'К разбору', description: 'Подпись KPI: события файла ирригации, которые правило не отнесло к поливу само (неоднозначные)' },
  kpiReviewUnit: { id: 'water.canon.kpi.reviewUnit', defaultMessage: 'событий', description: 'Единица рядом со значением KPI «К разбору»; одно слово' },
  kpiReviewNote: { id: 'water.canon.kpi.reviewNote', defaultMessage: 'вне плана: {unplanned} · вне агроцикла: {outside}', description: 'Подпись под KPI «К разбору»; {unplanned} — события вне плана полива (число), {outside} — вода вне агроцикла (м³); значения с единицами или «нет данных»' },
  kpiReviewMore: { id: 'water.canon.kpi.reviewMore', defaultMessage: '{count}+', description: 'Значение KPI «К разбору», когда очередь обрезана пределом сервера; {count} — предел' },
  leadDelivered: { id: 'water.canon.lead.delivered', defaultMessage: 'За агроцикл по файлу ирригации подано {delivered}.', description: 'Вторая часть строки-резюме, когда заявок на воду нет: вода агроцикла по событиям файла; {delivered} — объём (м³)' },
  feedDelivered: { id: 'water.canon.feed.delivered', defaultMessage: 'подано <n>{delivered}</n>', description: 'Подано в карточке полива по событиям файла; <n> — моноширинное число, {delivered} — объём (м³)' },
  feedNoDelivered: { id: 'water.canon.feed.noDelivered', defaultMessage: 'событий файла по поливу нет', description: 'Пояснение в карточке полива: у полива нет ни одного события в файле ирригации; строчными' },
  statDeliveredEventsNote: { id: 'water.canon.stat.deliveredEventsNote', defaultMessage: '{deliveries} по файлу ирригации', description: 'Мелкая подпись под «Подано» в сводке полива; {deliveries} — число подач словом' },
  statPairsDone: { id: 'water.canon.stat.pairsDone', defaultMessage: 'Полито пар', description: 'Подпись числа в сводке полива: пары «контур × полив», у которых есть событие в файле ирригации' },
  statPairsDoneNote: { id: 'water.canon.stat.pairsDoneNote', defaultMessage: 'из {total} · {area}', description: 'Мелкая подпись под «Полито пар»; {total} — число пар словом («2 468 пар»), {area} — земля с единицей' },
  pairsWord: { id: 'water.canon.stat.pairsWord', defaultMessage: '{count, plural, one {# пара} few {# пары} many {# пар} other {# пары}}', description: 'Число пар «контур × полив» словом: «2 468 пар»' },
  statNoDelivery: { id: 'water.canon.stat.noDelivery', defaultMessage: 'Без подачи', description: 'Подпись числа в сводке полива, окно которого закрылось: пары «контур × полив» без события в файле' },
  statWaiting: { id: 'water.canon.stat.waiting', defaultMessage: 'Ждём подачи', description: 'Подпись числа в сводке полива, окно которого идёт или впереди: пары «контур × полив», по которым события пока нет' },
  statNoDeliveryNote: { id: 'water.canon.stat.noDeliveryNote', defaultMessage: 'окно закрылось, события в файле нет', description: 'Мелкая подпись под «Без подачи»' },
  statWaitingNote: { id: 'water.canon.stat.waitingNote', defaultMessage: 'файл может опаздывать — это не «не полито»', description: 'Мелкая подпись под «Ждём подачи»' },
  cardVolumesEvents: { id: 'water.canon.detail.cardVolumesEvents', defaultMessage: 'подано {delivered}', description: 'Значение в карточке контура на карте по событиям файла; {delivered} — объём воды (м³) или «нет данных»' },
  cardFarm: { id: 'water.canon.detail.cardFarm', defaultMessage: 'Хозяйство', description: 'Заголовок строки в карточке контура на карте: хозяйство земли' },
  noFarmsReasonEvents: { id: 'water.canon.detail.noFarmsReasonEvents', defaultMessage: 'событий файла ирригации по этому поливу нет', description: 'Пояснение пустого состояния таблицы хозяйств полива по событиям файла; строчными, после заголовка' },
  // --- резюме
  leadRunning: {
    id: 'water.canon.lead.running',
    defaultMessage: 'Идёт <b>{watering}</b> агроцикла «{cycle}»: окно {window}{hasDays, select, yes {, до конца {days}} other {}}.',
    description: 'Строка-резюме под заголовком экрана «Вода», когда окно полива идёт; {watering} — название полива строчными, {cycle} — название агроцикла, {window} — окно («01–06 окт»), {days} — оставшиеся дни словом; <b> — выделение',
  },
  leadLast: {
    id: 'water.canon.lead.last',
    defaultMessage: 'Полив сейчас не идёт: последнее окно агроцикла «{cycle}» — <b>{watering}</b>, закрылось {date}.',
    description: 'Строка-резюме под заголовком экрана «Вода», когда окно не идёт, но есть закрытое; {cycle} — агроцикл, {watering} — название полива строчными, {date} — дата закрытия; <b> — выделение',
  },
  leadNone: {
    id: 'water.canon.lead.none',
    defaultMessage: 'В агроцикле «{cycle}» закрытых и идущих поливов на {date} нет.',
    description: 'Строка-резюме под заголовком экрана «Вода», когда нет ни идущих, ни закрытых поливов; {cycle} — агроцикл, {date} — дата состояния',
  },
  leadVolume: {
    id: 'water.canon.lead.volume',
    defaultMessage: 'За агроцикл подано {delivered} из {requested} запрошенных ({share}){hasShortfall, select, yes {, недоподача {shortfall}} other {}}.',
    description: 'Вторая часть строки-резюме: вода агроцикла; {delivered}/{requested} — объёмы (м³), {share} — доля подано от запрошенного (%), {shortfall} — недоподача (м³)',
  },
  // --- лента поливов
  feedLabel: { id: 'water.canon.feed.label', defaultMessage: 'Поливы агроцикла, свежие сверху', description: 'aria-label ленты поливов слева на экране «Вода»' },
  feedUpcoming: { id: 'water.canon.feed.upcoming', defaultMessage: 'Окно ещё не открылось: отчётов и подач по нему пока нет — это «ещё не срок».', description: 'Пояснение в карточке полива, окно которого впереди' },
  feedWindowNote: { id: 'water.canon.feed.windowNote', defaultMessage: 'окно идёт: пустой отчёт значит «ещё не срок»', description: 'Пометка под полосой выполнения идущего полива' },
  meterLabel: { id: 'water.canon.feed.meterLabel', defaultMessage: 'Подано {delivered} из {requested}', description: 'aria-label полосы покрытия полива; {delivered}/{requested} — объёмы воды (м³)' },
  meterFlow: { id: 'water.canon.feed.meterFlow', defaultMessage: '<n>{requested}</n> → <n>{delivered}</n>', description: 'Запрошено → подано в карточке полива; <n> — моноширинные числа, {requested}/{delivered} — объёмы (м³)' },
  meterCoverage: { id: 'water.canon.feed.meterCoverage', defaultMessage: 'покрытие <n>{value}</n>', description: 'Покрытие в карточке полива (подано от запрошенного); <n> — число, {value} — процент' },
  feedNoRequests: { id: 'water.canon.feed.noRequests', defaultMessage: 'Заявок на воду по поливу в срезе нет.', description: 'Пояснение в карточке полива: заявок на воду по этому поливу нет' },
  // --- хозяйства полива
  colFarm: { id: 'water.canon.farms.col.farm', defaultMessage: 'Хозяйство', description: 'Заголовок колонки таблицы хозяйств полива' },
  showOnMap: { id: 'water.canon.farms.showOnMap', defaultMessage: 'Показать контуры хозяйства на карте', description: 'Подсказка кнопки-значка в строке хозяйства: подсветить контуры хозяйства на карте полива' },
  colRequested: { id: 'water.canon.farms.col.requested', defaultMessage: 'Запрошено', description: 'Заголовок числовой колонки: запрошенный объём воды; узкая колонка' },
  colDelivered: { id: 'water.canon.farms.col.delivered', defaultMessage: 'Подано', description: 'Заголовок числовой колонки: поданный объём воды; узкая колонка' },
  colCoverage: { id: 'water.canon.farms.col.coverage', defaultMessage: 'Покрытие', description: 'Заголовок числовой колонки: подано от запрошенного, %; узкая колонка' },
  colShort: { id: 'water.canon.farms.col.short', defaultMessage: 'Не подано', description: 'Заголовок числовой колонки: недоподача хозяйства (м³); узкая колонка' },
  unitM3: { id: 'water.canon.unit.m3', defaultMessage: 'м³', description: 'Единица под заголовком колонки: кубометры воды' },
  // --- деталь полива
  reportsFeed: { id: 'water.canon.detail.reportsFeed', defaultMessage: 'в ленте слева', description: 'Подставляется в пояснение карты полива: где виден учёт отчётов о поливе — в ленте слева (экран «Вода»); начало фразы «он …»' },
  reportsProgress: { id: 'water.canon.detail.reportsProgress', defaultMessage: 'в блоке «Ход работы» выше', description: 'Подставляется в пояснение карты полива: где виден учёт отчётов о поливе — в блоке «Ход работы» на экране работы' },
  detailMore: { id: 'water.canon.detail.more', defaultMessage: 'Подробно о воде полива →', description: 'Ссылка в шапке выбранного полива: перейти к аналитике воды полива' },
  statRequested: { id: 'water.canon.stat.requested', defaultMessage: 'Запрошено', description: 'Подпись числа в сводке полива: запрошенный объём воды (м³)' },
  statDelivered: { id: 'water.canon.stat.delivered', defaultMessage: 'Подано', description: 'Подпись числа в сводке полива: поданный объём воды (м³)' },
  statDeliveredNote: { id: 'water.canon.stat.deliveredNote', defaultMessage: 'покрытие {coverage} · {deliveries}', description: 'Мелкая подпись под «Подано»; {coverage} — доля подано от запрошенного (%), {deliveries} — число подач словом' },
  statShort: { id: 'water.canon.stat.short', defaultMessage: 'Не подано', description: 'Подпись числа в сводке полива: недоподача по хозяйствам (м³)' },
  statShortNote: { id: 'water.canon.stat.shortNote', defaultMessage: 'сумма по хозяйствам{hasOver, select, yes {; перерасход {over}} other {}}', description: 'Мелкая подпись под «Не подано»; {over} — перерасход (м³), показывается, если он есть' },
  statWorks: { id: 'water.canon.stat.works', defaultMessage: 'Работ на полях', description: 'Подпись числа в сводке полива: сколько работ на полях поручено по этому поливу (поручение работы полю)' },
  statUnplanned: { id: 'water.canon.stat.unplanned', defaultMessage: 'Вне плана', description: 'Подпись числа в сводке полива: число подач воды вне плана полива' },
  statUnplannedNote: { id: 'water.canon.stat.unplannedNote', defaultMessage: 'подач вне плана полива', description: 'Мелкая подпись под «Вне плана»' },
  noContourDistribution: { id: 'water.canon.detail.noContourDistribution', defaultMessage: 'распределений подачи по контурам у этого полива нет', description: 'Сообщение на месте карты полива: подачи по контурам не распределены; строчными' },
  mapCaption: { id: 'water.canon.detail.mapCaption', defaultMessage: '{watering}: подано, частично, не подано — по распределениям подачи на контуры', description: 'Подпись карты полива для программ чтения с экрана; {watering} — название полива' },
  cardThisWatering: { id: 'water.canon.detail.cardThis', defaultMessage: 'Этот полив', description: 'Заголовок строки в карточке контура на карте: сведения по выбранному поливу' },
  cardVolumes: { id: 'water.canon.detail.cardVolumes', defaultMessage: 'запрошено {requested}, подано {delivered}', description: 'Значение в карточке контура на карте; {requested}/{delivered} — объёмы воды (м³) или «нет данных»' },
  cardRequest: { id: 'water.canon.detail.cardRequest', defaultMessage: 'Заявка хозяйства', description: 'Заголовок строки в карточке контура на карте: хозяйство, подавшее заявку' },
  mapInfoLabel: { id: 'water.canon.detail.mapInfoLabel', defaultMessage: 'Что на карте полива', description: 'aria-label и заголовок кнопки-«i» рядом с картой полива' },
  mapInfoBody: { id: 'water.canon.detail.mapInfoBody', defaultMessage: 'Заливка — вода по распределениям этого полива; контуры без распределения скрыты. Отчёт о поливе — отдельный учёт: он {where}.', description: 'Текст всплывающего пояснения карты полива; {where} — где виден учёт отчётов («в ленте слева» или «в блоке «Ход работы» выше»)' },
  farmsOfWatering: { id: 'water.canon.detail.farmsTitle', defaultMessage: 'Хозяйства этого полива', description: 'Подзаголовок таблицы хозяйств выбранного полива' },
  onMapChip: { id: 'water.canon.detail.onMapChip', defaultMessage: 'На карте: {name} ✕', description: 'Чип активного выделения хозяйства на карте полива (нажатие снимает); {name} — хозяйство' },
  noFarmsTitle: { id: 'water.canon.detail.noFarmsTitle', defaultMessage: 'Хозяйств нет', description: 'Заголовок пустого состояния таблицы хозяйств полива' },
  noFarmsReason: { id: 'water.canon.detail.noFarmsReason', defaultMessage: 'заявок на этот полив в срезе нет', description: 'Пояснение пустого состояния таблицы хозяйств полива; строчными, после заголовка' },
  channelsTitle: { id: 'water.canon.detail.channelsTitle', defaultMessage: 'Каналы этого полива: канал → хозяйства', description: 'Подзаголовок списка каналов выбранного полива' },
  // --- сверка и журнал
  colWatering: { id: 'water.canon.mismatch.col.watering', defaultMessage: 'Полив', description: 'Заголовок колонки таблицы расхождений сверки: полив' },
  colChannel: { id: 'water.canon.mismatch.col.channel', defaultMessage: 'Канал', description: 'Заголовок колонки таблицы расхождений сверки: канал' },
  channelUnnamed: { id: 'water.canon.mismatch.channelUnnamed', defaultMessage: 'канал не назван', description: 'Значение колонки «Канал», когда канал не назван в данных' },
  colPeriod: { id: 'water.canon.mismatch.col.period', defaultMessage: 'Период', description: 'Заголовок колонки таблицы расхождений сверки: период подачи' },
  colVolume: { id: 'water.canon.mismatch.col.volume', defaultMessage: 'Объём', description: 'Заголовок числовой колонки: объём подачи (м³)' },
  colDiff: { id: 'water.canon.mismatch.col.diff', defaultMessage: 'Разница', description: 'Заголовок числовой колонки: разница между суммой распределений и итогом (м³)' },
  colRecon: { id: 'water.canon.mismatch.col.recon', defaultMessage: 'Сверка', description: 'Заголовок колонки: итог сверки подачи' },
  wateringFallback: { id: 'water.canon.mismatch.wateringFallback', defaultMessage: 'полив', description: 'Название полива в таблице расхождений, если полив не найден в списке; строчными' },
  noMismatchTitle: { id: 'water.canon.mismatch.emptyTitle', defaultMessage: 'Расхождений нет', description: 'Заголовок пустого состояния таблицы расхождений сверки' },
  noMismatchReason: { id: 'water.canon.mismatch.emptyReason', defaultMessage: 'у всех подач поливов агроцикла сумма распределений равна итогу', description: 'Пояснение пустого состояния таблицы расхождений; строчными, после заголовка' },
  colRequestFarm: { id: 'water.canon.ledger.col.farm', defaultMessage: 'Хозяйство', description: 'Заголовок колонки журнала заявок: хозяйство, подавшее заявку' },
  nameUnknown: { id: 'water.canon.ledger.nameUnknown', defaultMessage: 'имя не названо', description: 'Вместо имени хозяйства в журнале заявок, когда имя не названо' },
  colSubmitted: { id: 'water.canon.ledger.col.submitted', defaultMessage: 'Подана', description: 'Заголовок колонки журнала заявок: дата подачи заявки (заявка подана); узкая колонка' },
  colDesired: { id: 'water.canon.ledger.col.desired', defaultMessage: 'Желаемый срок', description: 'Заголовок колонки журнала заявок: срок, когда хозяйство хотело получить воду' },
  desiredNone: { id: 'water.canon.ledger.desiredNone', defaultMessage: 'не указан', description: 'Значение колонки «Желаемый срок», когда срок не указан' },
  colContours: { id: 'water.canon.ledger.col.contours', defaultMessage: 'Контуров', description: 'Заголовок числовой колонки журнала заявок: число контуров в заявке; узкая колонка' },
  ledgerEmptyTitle: { id: 'water.canon.ledger.emptyTitle', defaultMessage: 'Заявок в срезе нет', description: 'Заголовок пустого состояния журнала заявок на воду' },
  ledgerLoadWhat: { id: 'water.canon.ledger.loadWhat', defaultMessage: 'журнал заявок', description: 'Что не загрузилось, в винительном падеже, для сообщения «Не удалось загрузить …»; также подпись скелетона таблицы' },
  prev: { id: 'water.canon.ledger.prev', defaultMessage: '← Назад', description: 'Кнопка постраничной навигации журнала заявок: предыдущая страница; коротко' },
  next: { id: 'water.canon.ledger.next', defaultMessage: 'Далее →', description: 'Кнопка постраничной навигации журнала заявок: следующая страница; коротко' },
  pageRange: { id: 'water.canon.ledger.range', defaultMessage: '{from}–{to} из {total}', description: 'Диапазон строк журнала заявок; {from}/{to}/{total} — числа, уже отформатированные' },
  // --- скелетоны
  skelBodyLabel: { id: 'water.canon.skeleton.body', defaultMessage: 'воду агроцикла', description: 'Подпись области загрузки экрана «Вода» (для программ чтения с экрана); в винительном падеже' },
  skelWaterings: { id: 'water.canon.skeleton.waterings', defaultMessage: 'Поливы агроцикла', description: 'Заголовок карточки поливов на экране «Вода» (и её скелетона): лента поливов агроцикла и деталь выбранного' },
  skelFarmsChart: { id: 'water.canon.skeleton.farmsChart', defaultMessage: 'Недоподача и перерасход по хозяйствам', description: 'Заголовок диаграммы разрыва по хозяйствам (и её скелетона)' },
  skelChannels: { id: 'water.canon.skeleton.channels', defaultMessage: 'Каналы', description: 'Заголовок скелетона графика каналов выбранного полива' },
  // --- экран
  trailDistrict: { id: 'water.canon.trail.level', defaultMessage: 'Район', description: 'Уровень хлебных крошек: район (для имени района «Бука»)' },
  trailDistrictName: { id: 'water.canon.trail.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в цепочке навигации; географическое имя — транслитерация' },
  trailSection: { id: 'water.canon.trail.section', defaultMessage: 'Раздел', description: 'Уровень хлебных крошек: раздел приложения' },
  pageTitle: { id: 'water.canon.page.title', defaultMessage: 'Вода', description: 'Заголовок экрана «Вода» (хоким, менеджер); также последний элемент хлебных крошек' },
  pagePurpose: { id: 'water.canon.page.purpose', defaultMessage: 'Поливы агроцикла как работы плана: какое окно идёт, куда дошла вода и где поливы остались без подачи.', description: 'Подзаголовок экрана «Вода»: для чего экран' },
  stateOn: { id: 'water.canon.page.stateOn', defaultMessage: 'Состояние на {date}', description: 'Подпись метки достоверности в шапке экрана; {date} — дата состояния' },
  moreWater: { id: 'water.canon.page.moreWater', defaultMessage: 'Подробно о воде', description: 'Ссылка в шапке экрана «Вода» на раздел аналитики воды' },
  cycleFallback: { id: 'water.canon.page.cycleFallback', defaultMessage: 'агроцикл', description: 'Подстановка названия агроцикла в фразах, пока агроцикл не выбран; строчными' },
  cyclesLoadWhat: { id: 'water.canon.page.cyclesLoadWhat', defaultMessage: 'агроциклы сезона', description: 'Что не загрузилось, в винительном падеже, для сообщения «Не удалось загрузить …»' },
  noCyclesTitle: { id: 'water.canon.page.noCyclesTitle', defaultMessage: 'Агроциклов в срезе нет', description: 'Заголовок пустого состояния экрана «Вода»: в срезе нет агроциклов' },
  noCyclesReason: { id: 'water.canon.page.noCyclesReason', defaultMessage: 'сервер не вернул ни одного агроцикла', description: 'Пояснение пустого состояния, когда сервер не дал причину; строчными, после заголовка' },
  wateringsLoadWhat: { id: 'water.canon.page.wateringsLoadWhat', defaultMessage: 'поливы агроцикла', description: 'Что не загрузилось, в винительном падеже, для сообщения «Не удалось загрузить …»' },
  noWateringsTitle: { id: 'water.canon.page.noWateringsTitle', defaultMessage: 'Поливов в этом агроцикле нет', description: 'Заголовок пустого состояния: у выбранного агроцикла нет поливов' },
  noWateringsReason: { id: 'water.canon.page.noWateringsReason', defaultMessage: 'работ вида «полив» в сводке агроцикла нет — это пробел шаблона, а не ноль поливов', description: 'Пояснение пустого состояния, когда сервер не дал причину; строчными, после заголовка' },
  deliveriesLoadWhat: { id: 'water.canon.page.deliveriesLoadWhat', defaultMessage: 'подачи полива', description: 'Что не загрузилось, в винительном падеже, для сообщения «Не удалось загрузить …»' },
  noWateringPickedTitle: { id: 'water.canon.page.noWateringPickedTitle', defaultMessage: 'Полив не выбран', description: 'Заголовок пустого состояния детали полива' },
  noWateringPickedReason: { id: 'water.canon.page.noWateringPickedReason', defaultMessage: 'выберите полив в ленте слева', description: 'Пояснение пустого состояния детали полива; строчными, после заголовка' },
  farmsChartLabel: { id: 'water.canon.page.farmsChartLabel', defaultMessage: 'разрез по хозяйствам', description: 'Подпись области загрузки диаграммы разрыва по хозяйствам (для программ чтения с экрана)' },
  farmsChartAside: { id: 'water.canon.page.farmsChartAside', defaultMessage: 'все поливы агроцикла · {farms}', description: 'Пояснение в шапке диаграммы разрыва; {farms} — число хозяйств словом («12 хозяйств»)' },
  mismatchesTitle: { id: 'water.canon.page.mismatchesTitle', defaultMessage: 'Расхождения сверки по поливам', description: 'Подзаголовок таблицы расхождений сверки' },
  mismatchesLabel: { id: 'water.canon.page.mismatchesLabel', defaultMessage: 'расхождения сверки', description: 'Подпись скелетона таблицы расхождений сверки (для программ чтения с экрана)' },
  unplannedTitle: { id: 'water.canon.page.unplannedTitle', defaultMessage: 'Полив вне плана', description: 'Подзаголовок списка поливов, по которым были подачи вне плана полива' },
  unplannedNone: { id: 'water.canon.page.unplannedNone', defaultMessage: 'Подач вне плана полива в агроцикле нет: все распределения закрыли работы плана.', description: 'Пояснение, когда подач вне плана полива нет' },
  unplannedItem: { id: 'water.canon.page.unplannedItem', defaultMessage: '{watering}: {count} вне плана', description: 'Пункт списка поливов с подачами вне плана; {watering} — название полива, {count} — число подач вне плана' },
  journalTitle: { id: 'water.canon.page.journalTitle', defaultMessage: 'Журнал заявок на воду', description: 'Подзаголовок журнала заявок хозяйств на воду' },
  refTitle: { id: 'water.canon.page.refTitle', defaultMessage: 'Справка: каналы и вода вне плана', description: 'Заголовок свёрнутого по умолчанию блока внизу экрана «Вода» (вода по файлу ирригации): пять крупнейших каналов, вода вне плана, вне агроцикла и без земли' },
  refTitleRequests: { id: 'water.canon.page.refTitleRequests', defaultMessage: 'Справка: каналы, сверка и журнал заявок', description: 'Заголовок свёрнутого по умолчанию блока внизу экрана «Вода», когда в данных есть заявки на воду: каналы, вода вне плана, сверка подач и журнал заявок' },
  refSummaryRequests: { id: 'water.canon.page.refSummaryRequests', defaultMessage: '{district} · {reconcile}', description: 'Сводка свёрнутой справки при заявках; {district} — каналы и вода вне плана района, {reconcile} — расхождения сверки и подачи вне плана' },
});

const STATUS_WORDS: Record<IrrigationWork['status'], Message> = {
  planned: m.statusPlanned,
  announced: m.statusAnnounced,
  active: m.statusActive,
  paused: m.statusPaused,
  completed: m.statusCompleted,
  cancelled: m.statusCancelled,
};

const RECONCILIATION_WORDS: Record<DeliveryView['reconciliation'], Message> = {
  matched: m.reconMatched,
  mismatched: m.reconMismatched,
  unverifiable: m.reconUnverifiable,
};

/** Ключ зависимостей запроса: объект параметров сравнивается по значению. */
export function paramsKey(params: QueryParams): string {
  return JSON.stringify(params);
}

/** Объём словами: отсутствие числа — «нет данных», а не «0 м³». */
export function volume(value: number | null): string {
  return value === null ? tr(m.noData) : m3(value);
}

/** Ошибка запроса — состояние экрана с причиной, а не пустая таблица. */
export function RequestGate<T>({
  res,
  what,
  children,
  skeleton,
}: {
  res: RequestResult<T>;
  what: string;
  children: (data: T) => ReactNode;
  /** Скелетон в размер готового блока (6al.49); по умолчанию — строки текста. */
  skeleton?: ReactNode;
}) {
  if (res.data === null) {
    if (res.state === 'error') {
      return <LoadError compact what={what} error={res.error} onRetry={res.reload} />;
    }
    // «Нет данных» до конца загрузки не говорим: скелетон (6al.49).
    return <>{skeleton ?? <SkeletonRegion label={what} minHeight={72}><SkeletonText lines={3} /></SkeletonRegion>}</>;
  }
  return <>{children(res.data)}</>;
}

/* ------------------------------------------------------- выбор полива */

export interface IrrigationChoice {
  cycles: RequestResult<unknown>;
  picture: RequestResult<unknown>;
  choices: CycleChoice[];
  cycle: CycleChoice | null;
  works: IrrigationWork[];
  work: IrrigationWork | null;
  /** Дата состояния: `as_of` анализа или «сегодня» сервера (`horizon`); пока циклы не прочитаны — пусто. */
  ref: string;
  /** Оговорка сервера о происхождении водного баланса выбранного цикла. */
  waterNote: string | null;
  /** Вода цикла по картине сезона — та же доля, что в «Обзоре района». */
  share: CycleWaterShare | null;
  /** Почему циклов нет — словами сервера. */
  cyclesEmptyNote: string | null;
  setCycle: (cycle: string) => void;
  setWork: (key: string) => void;
}

/**
 * Цикл и полив в адресе (`?cycle=2026:COTTON&work=132:37`): выбор переживает
 * перезагрузку и пересылку ссылки. Неизвестное значение в адресе не ломает
 * экран — выбирается умолчание (активный цикл, полив, чьё окно идёт на дату
 * состояния, иначе последний закрывшийся).
 */
export function useIrrigationChoice(params: QueryParams): IrrigationChoice {
  const [search, setSearch] = useSearchParams();
  const key = paramsKey(params);
  const cycles = useRequest(() => api.seasonCycles(params), [key]);
  // Дата среза — названная контекстом либо «сегодня» сервера из ответа (`horizon`), не часы браузера.
  const ref = typeof params.as_of === 'string' && params.as_of ? params.as_of : (cycles.data?.horizon ?? '');
  const choices = useMemo(() => (cycles.data ? cycleChoices(cycles.data) : []), [cycles.data]);
  const cycle = pickCycle(choices, search.get('cycle'), ref);
  const cycleKey = cycle?.cycle ?? null;

  // Сводка картины нужна ради поливов цикла и их осей отчётов: недели не просим.
  const picture = useRequest(
    () =>
      cycleKey
        ? api.seasonPicture({ ...params, cycle: cycleKey, summary: true, weeks: 0 })
        : Promise.resolve(null),
    [key, cycleKey],
  );
  const works = useMemo(() => (picture.data ? irrigationWorks(picture.data) : []), [picture.data]);
  const work = pickWork(works, search.get('work'), ref);

  const patch = (next: Record<string, string | null>) =>
    setSearch(
      (previous) => {
        const copy = new URLSearchParams(previous);
        for (const [name, value] of Object.entries(next)) {
          if (value === null) copy.delete(name);
          else copy.set(name, value);
        }
        return copy;
      },
      { replace: true },
    );

  return {
    cycles,
    picture,
    choices,
    cycle,
    works,
    work,
    ref,
    waterNote: picture.data?.water_note ?? null,
    share: cycleWaterShare(picture.data),
    cyclesEmptyNote: cycles.data?.empty_note || null,
    // Смена цикла сбрасывает полив и канал: работа одного цикла в другом не живёт.
    setCycle: (value) => patch({ cycle: value, work: null, channel: null }),
    setWork: (value) => patch({ work: value, channel: null }),
  };
}

/** Селекторы цикла и полива — общие для `/water` и `/analysis/water`. */
export function IrrigationPicker({ choice }: { choice: IrrigationChoice }) {
  const t = useT();
  if (choice.choices.length === 0) return null;
  return (
    <div className="chips" style={{ alignItems: 'flex-end' }}>
      <ChoiceSelect
        label={t(m.cycleLabel)}
        value={choice.cycle?.cycle ?? ''}
        options={choice.choices.map((c) => ({ id: c.cycle, name: c.name }))}
        onChange={choice.setCycle}
      />
      {choice.works.length > 0 ? (
        <ChoiceSelect
          label={t(m.wateringLabel)}
          value={choice.work ? workKey(choice.work) : ''}
          options={choice.works.map((w) => ({
            id: workKey(w),
            name: w.name,
            description: t(m.pickerOption, { window: windowLabel(w.windowFrom, w.windowTo), status: t(STATUS_WORDS[w.status]) }),
          }))}
          onChange={choice.setWork}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------- вода поливов */

export interface WorkWater {
  work: IrrigationWork;
  view: WaterWorkView;
  raw: WaterWork;
}

/**
 * Вода ВЫБРАННОГО полива одним запросом: хозяйства, каналы → хозяйства,
 * контуры и недоподача приходят готовыми (С4). Остальные поливы цикла браузер
 * не читает: их покрытие и сверку даёт `useCycleWater`.
 */
function useWorkWater(params: QueryParams, work: IrrigationWork | null): RequestResult<WorkWater | null> {
  return useRequest(async () => {
    if (!work) return null;
    const reply = await api.waterWork(work.workDefId, { ...params, campaign_id: work.campaignId });
    return { work, view: toWaterWork(reply), raw: reply };
  }, [paramsKey(params), work ? workKey(work) : '']);
}

/** Вода цикла с сервера: покрытие по поливам, подачи и сверка, хозяйства цикла. */
function useCycleWater(params: QueryParams, cycle: string | null): RequestResult<CycleWaterView | null> {
  return useRequest(
    async () => (cycle ? toCycleWater(await api.waterCycle({ ...params, cycle })) : null),
    [paramsKey(params), cycle ?? ''],
  );
}

/* ------------------------------------------------------- слова полива */

function stateBadge(work: IrrigationWork, state: WateringState): { text: string; tone: 'aqua' | 'neutral' | 'outline' } {
  const left = work.daysLeft;
  if (state === 'active')
    return { text: left !== null && left >= 0 ? tr(m.badgeActiveLeft, { days: countOf(left, m.days) }) : tr(m.badgeActive), tone: 'aqua' };
  if (state === 'done') return { text: tr(m.badgeDone), tone: 'neutral' };
  if (state === 'upcoming') return { text: tr(m.badgeUpcoming), tone: 'outline' };
  return { text: tr(m.badgeNoWindow), tone: 'outline' };
}

const FARM_STATE: Record<FarmWaterState, Message> = {
  delivered: m.farmDelivered,
  partial: m.farmPartial,
  none: m.farmNone,
  unmeasured: m.farmUnmeasured,
};

/* -------------------------------------------------------------- KPI */

function WaterKpis({
  works,
  day,
  water,
  share,
  cycleName,
  events,
  district,
  review,
}: {
  works: IrrigationWork[];
  day: string;
  water: CycleWaterView | null;
  share: CycleWaterShare | null;
  cycleName: string;
  /** Вода по событиям файла (заявок нет): запрошено, недоподача и сверка не показываются. */
  events: boolean;
  district: WaterDistrict | null;
  review: number | null;
}) {
  const t = useT();
  const running = works.find((w) => wateringState(w, day) === 'active') ?? null;
  const closed = works.filter((w) => wateringState(w, day) === 'done');
  const last = closed[closed.length - 1] ?? null;
  const focus = running ?? last;
  const total = water;
  const points: CoveragePoint[] = water
    ? works.map((w) => ({
        key: workKey(w),
        label: wateringTitle(w.name).title,
        // Покрытие каждого полива — поле ответа сервера, не частное в браузере.
        coveragePct: water.works.get(workKey(w))?.coveragePct ?? null,
        current: w === focus,
      }))
    : [];
  const silent = focus ? focus.noReport : null;
  const dm = (id: string) => districtMetric(district, id);
  const noDeliveryPairs = district?.metrics.find((x) => x.id === 'water.no_delivery_pairs')?.value ?? null;

  const runningCard = (
      <KpiCard
        // Без идущего окна — факт «последний полив», а не отрицание «полив не
        // идёт» с номером полива в значении: подпись не спорит с числом.
        label={running ? t(m.kpiRunning, { watering: wateringTitle(running.name).title.toLowerCase() }) : last ? t(m.kpiLast) : t(m.kpiIdle)}
        icon={Timer}
        value={running ? num(Math.max(0, running.daysLeft ?? 0)) : last ? wateringTitle(last.name).title : t(m.kpiNone)}
        unit={running ? plural(Math.max(0, running.daysLeft ?? 0), m.dayWord) : undefined}
        note={
          running
            ? t(m.kpiRunningNote, { date: dateShort(running.windowTo) })
            : last
              ? t(m.kpiLastNote, { date: dateShort(last.windowTo) })
              : t(m.kpiNoWindows)
        }
      />
  );

  if (events) {
    // Четыре ключевых числа (журнал №1450): подано, полито по земле, без подачи,
    // к разбору. Идёт ли полив — строкой-резюме в шапке и лентой поливов.
    return (
      <Grid cols={4}>
        <KpiCard
          label={t(m.kpiDelivered)}
          icon={Droplets}
          value={share ? (share.deliveredM3 === null ? t(m.noData) : m3Compact(share.deliveredM3)) : '…'}
          note={t(m.kpiDeliveredNote, { value: dm('water.m3_per_irrigated_ha') })}
        />
        <KpiCard
          label={t(m.kpiDoneLand)}
          icon={Layers}
          value={dm('water.irrigation_done_land_pct')}
          note={t(m.kpiDoneLandNote, { land: dm('water.irrigated_land_ha'), pairs: dm('water.irrigation_done_pairs_pct') })}
        />
        <KpiCard
          label={t(m.kpiNoDelivery)}
          icon={FileSearch}
          tone={noDeliveryPairs !== null && noDeliveryPairs > 0 ? 'warning' : undefined}
          value={dm('water.no_delivery_land_ha')}
          note={t(m.kpiNoDeliveryNote, { pairs: dm('water.no_delivery_pairs'), waiting: dm('water.irrigation_waiting_land_ha') })}
        />
        <KpiCard
          label={t(m.kpiReview)}
          icon={Scale}
          to="/ops/water"
          tone={review !== null && review > 0 ? 'warning' : undefined}
          value={review === null ? '…' : review >= REVIEW_CAP ? t(m.kpiReviewMore, { count: num(REVIEW_CAP) }) : num(review)}
          unit={review === null ? undefined : t(m.kpiReviewUnit)}
          note={t(m.kpiReviewNote, { unplanned: dm('water.unplanned_events'), outside: dm('water.outside_cycle_m3') })}
        />
      </Grid>
    );
  }

  return (
    <Grid cols={4}>
      {runningCard}
      <KpiCard
        label={t(m.kpiWater)}
        icon={Gauge}
        value={share ? (share.sharePct === null ? t(m.kpiNoRequests) : pct(share.sharePct, 1)) : '…'}
        note={
          share ? (
            <span className="wv-kpinote" title={waterShareMethod()}>
              {total ? <CoverageStrip points={points} /> : null}
              <span>
                {t(m.kpiWaterNote, {
                  words: waterShareWords(),
                  state: share.shortfallM3 === null ? 'unknown' : share.shortfallM3 > 0 ? 'some' : 'none',
                  value: share.shortfallM3 !== null && share.shortfallM3 > 0 ? m3Compact(share.shortfallM3) : '',
                  cycle: cycleName,
                })}
              </span>
            </span>
          ) : (
            t(m.kpiReading)
          )
        }
      />
      <KpiCard
        label={running ? t(m.kpiSilentRunning) : t(m.kpiSilent)}
        icon={FileMinus}
        value={silent === null ? t(m.kpiNone) : num(silent)}
        unit={focus ? t(m.kpiOf, { works: countOf(focus.obligations, WORDS.obligation) }) : undefined}
        note={
          focus
            ? running
              ? t(m.kpiSilentRunningNote)
              : t(m.kpiSilentNote, { watering: wateringTitle(focus.name).title })
            : t(m.kpiNoWindows)
        }
      />
      <KpiCard
        label={t(m.kpiMismatch)}
        icon={Scale}
        value={total ? num(total.mismatched) : '…'}
        unit={total ? t(m.kpiOf, { works: countOf(total.deliveries, m.deliveries) }) : undefined}
        note={t(m.kpiMismatchNote)}
      />
    </Grid>
  );
}

/** Строка-резюме одной фразой: какое окно идёт и как идёт вода цикла. */
function Lead({ works, day, share, cycleName }: { works: IrrigationWork[]; day: string; share: CycleWaterShare | null; cycleName: string }) {
  const t = useT();
  const bold = (c: ReactNode) => <strong>{c}</strong>;
  const running = works.find((w) => wateringState(w, day) === 'active') ?? null;
  const closed = works.filter((w) => wateringState(w, day) === 'done');
  const last = closed[closed.length - 1] ?? null;
  return (
    <div className="wlead">
      <Icon icon={Droplets} size={18} />
      <span className="wlead__text">
        {running ? (
          <>
            {t(m.leadRunning, {
              b: bold,
              watering: wateringTitle(running.name).title.toLowerCase(),
              cycle: cycleName,
              window: windowLabel(running.windowFrom, running.windowTo),
              hasDays: running.daysLeft !== null && running.daysLeft >= 0 ? 'yes' : 'no',
              days: running.daysLeft !== null && running.daysLeft >= 0 ? countOf(running.daysLeft, m.days) : '',
            })}
          </>
        ) : last ? (
          <>
            {t(m.leadLast, { b: bold, cycle: cycleName, watering: wateringTitle(last.name).title.toLowerCase(), date: dateShort(last.windowTo) })}
          </>
        ) : (
          <>{t(m.leadNone, { cycle: cycleName, date: dateShort(day) })}</>
        )}{' '}
        {share && share.requestedM3 === null && share.deliveredM3 !== null ? (
          <>{t(m.leadDelivered, { delivered: m3Compact(share.deliveredM3) })}</>
        ) : null}
        {share && share.requestedM3 !== null ? (
          <>
            {t(m.leadVolume, {
              delivered: m3Compact(share.deliveredM3),
              requested: m3Compact(share.requestedM3),
              share: pct(share.sharePct, 1),
              hasShortfall: share.shortfallM3 !== null && share.shortfallM3 > 0 ? 'yes' : 'no',
              shortfall: share.shortfallM3 !== null && share.shortfallM3 > 0 ? m3Compact(share.shortfallM3) : '',
            })}
          </>
        ) : null}
      </span>
    </div>
  );
}

/* ------------------------------------------------------- лента поливов */

function WateringFeed({
  works,
  day,
  selected,
  water,
  onPick,
  events,
  compact = false,
}: {
  works: IrrigationWork[];
  day: string;
  selected: IrrigationWork | null;
  water: CycleWaterView | null;
  onPick: (key: string) => void;
  events: boolean;
  /** Та же лента в панели развёрнутой карты: плотнее (журнал №1390). */
  compact?: boolean;
}) {
  const t = useT();
  const num_ = (c: ReactNode) => <span className="num">{c}</span>;
  // Свежее сверху: последний полив интереснее первого.
  const feed = [...works].reverse();
  return (
    <ol className={compact ? 'wv-feed wv-feed--compact' : 'wv-feed'} aria-label={t(m.feedLabel)}>
      {feed.map((w) => {
        const key = workKey(w);
        const state = wateringState(w, day);
        const badge = stateBadge(w, state);
        const name = wateringTitle(w.name);
        const b = water?.works.get(key) ?? null;
        const ratio = b?.coveragePct ?? null;
        const on = selected !== null && workKey(selected) === key;
        return (
          <li key={key} className="wv-feed__item">
            <span className={`wfeed__dot wfeed__dot--${state === 'active' ? 'active' : state === 'done' ? 'done' : 'upcoming'}`} aria-hidden="true" />
            <div className={`wv-card ${on ? 'is-on' : ''}`} onClick={() => onPick(key)}>
              <button type="button" className="wv-card__pick focus-ring" aria-pressed={on} onClick={(e) => { e.stopPropagation(); onPick(key); }}>
                <span className="wv-card__name">{name.title}</span>
                <Badge tone={badge.tone}>{badge.text}</Badge>
              </button>
              <div className="wv-card__win">
                {windowLabel(w.windowFrom, w.windowTo)}
                {name.note ? ` · ${name.note}` : ''}
              </div>
              {state === 'upcoming' ? (
                <p className="t-caption">{t(m.feedUpcoming)}</p>
              ) : (
                <DoneSplit
                  size="sm"
                  tone="water"
                  counts={{ total: w.obligations, reported: w.reported }}
                  denominator={m.wateringWorksDenominator}
                  windowNote={state === 'active' ? t(m.feedWindowNote) : undefined}
                />
              )}
              {events ? (
                state === 'upcoming' || !b ? null : b.deliveredM3 === null ? (
                  <p className="t-caption">{t(m.feedNoDelivered)}</p>
                ) : (
                  <div className="wmeter__facts">
                    <span>{t(m.feedDelivered, { n: num_, delivered: m3Compact(b.deliveredM3) })}</span>
                  </div>
                )
              ) : b && b.requestedM3 !== null ? (
                <div className="wmeter">
                  <div className="wmeter__bar" role="img" aria-label={t(m.meterLabel, { delivered: m3(b.deliveredM3), requested: m3(b.requestedM3) })}>
                    <div className={`wmeter__fill ${ratio !== null && ratio >= 100 ? 'wmeter__fill--full' : ''}`} style={{ width: `${Math.min(100, ratio ?? 0)}%` }} />
                  </div>
                  <div className="wmeter__facts">
                    <span>{t(m.meterFlow, { n: num_, requested: m3Compact(b.requestedM3), delivered: m3Compact(b.deliveredM3) })}</span>
                    <span>{t(m.meterCoverage, { n: num_, value: pct(ratio, 1) })}</span>
                  </div>
                </div>
              ) : water ? (
                <p className="t-caption">{t(m.feedNoRequests)}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* --------------------------------------------------- выбранный полив */

const farmColumns = (onShow: (row: FarmWaterRow) => void, focusId: number | null, events: boolean): Column<FarmWaterRow>[] => [
  {
    key: 'farm',
    header: tr(m.colFarm),
    render: (r) => (
      <span className="wv-farm">
        <span className="wcell">
          <Link to={`/farms/${r.actorId}`}>{r.name}</Link>
          <span className="wcell__sub">{countOf(r.contourIds.length, WORDS.contour)}</span>
        </span>
        <button
          type="button"
          className={`wv-farm__map focus-ring ${focusId === r.actorId ? 'is-on' : ''}`}
          aria-pressed={focusId === r.actorId}
          title={tr(m.showOnMap)}
          onClick={() => onShow(r)}
        >
          <Icon icon={MapIcon} size={16} />
        </button>
      </span>
    ),
  },
  ...(events ? [] : [{ key: 'req', header: tr(m.colRequested), unit: tr(m.unitM3), numeric: true, render: (r: FarmWaterRow) => (r.requestedM3 === null ? tr(m.noData) : num(Math.round(r.requestedM3))) }]),
  { key: 'del', header: tr(m.colDelivered), unit: tr(m.unitM3), numeric: true, render: (r) => (r.deliveredM3 === null ? tr(m.noData) : num(Math.round(r.deliveredM3))) },
  ...(events ? [] : [{
    key: 'cov',
    header: tr(m.colCoverage),
    numeric: true,
    render: (r: FarmWaterRow) => (
      <span className="wcell wcell--num">
        <span>{pct(r.coveragePct, 1)}</span>
        <span className={`wv-state wv-state--${r.state}`}>{tr(FARM_STATE[r.state])}</span>
      </span>
    ),
  } satisfies Column<FarmWaterRow>,
  { key: 'short', header: tr(m.colShort), unit: tr(m.unitM3), numeric: true, render: (r: FarmWaterRow) => (r.shortM3 === null ? tr(m.noData) : r.shortM3 > 0 ? num(Math.round(r.shortM3)) : '0') }]),
];

export function WateringDetail({
  item,
  day,
  channel,
  setChannel,
  linkSearch,
  cycle,
  reportsAt,
  reportsInProgress = false,
  events: eventsProp,
  mapPanel,
}: {
  item: WorkWater;
  day: string;
  channel: string | null;
  setChannel: (key: string | null) => void;
  linkSearch: (extra?: Record<string, string | number>) => string;
  cycle: string;
  /** Где на этом экране учёт отчётов о поливе: на «Воде» — лента слева, на экране работы — «Ход работы». Не задано — лента слева. */
  reportsAt?: string;
  /** То же без готовой фразы: учёт отчётов — в блоке «Ход работы» выше (экран работы). */
  reportsInProgress?: boolean;
  /** Вода по событиям файла: «запрошено», покрытие и недоподача не показываются. Не задано — по данным полива: заявок нет. */
  events?: boolean;
  /** Панель управления карты в развёрнутом кадре: выбор полива без выхода из полноэкранного режима. */
  mapPanel?: MapPanel;
}) {
  const t = useT();
  const { work, view } = item;
  const where = reportsAt ?? (reportsInProgress ? t(m.reportsProgress) : t(m.reportsFeed));
  const [focusFarm, setFocusFarm] = useState<number | null>(null);
  useEffect(() => setFocusFarm(null), [work, channel]);
  const timeline = useAsOfTimeline({ from: work.windowFrom ?? undefined });

  // Хозяйства, каналы → хозяйства, контуры и недоподача — строки сервера (С4):
  // выбор канала — выбор готовой строки, а не пересчёт распределений.
  const channels = view.channels;
  const channelRow = channel ? channels.find((c) => c.key === channel) ?? null : null;
  const farms = channelRow?.farms ?? view.farms;
  const gap = (channelRow ? channelRow.shortfall : view.shortfall) ?? null;
  const contourRows = channelRow?.contours ?? view.contours;
  const waterById = useMemo(
    () => Object.fromEntries(contourRows.map((c) => [String(c.contourId), c.state])),
    [contourRows],
  );
  const contourIds = useMemo(() => Object.keys(waterById), [waterById]);
  const focusRow = focusFarm !== null ? farms.find((f) => f.actorId === focusFarm) ?? null : null;
  const byContour = useMemo(() => new Map(contourRows.map((c) => [String(c.contourId), c])), [contourRows]);

  const state = wateringState(work, day);
  const badge = stateBadge(work, state);
  const name = wateringTitle(work.name);
  const b = view.balance;
  const ratio = b.coveragePct;
  const events = eventsProp ?? (b.requests === 0 && b.requestedM3 === null);

  return (
    <div className="wv-detail" data-guide="detail">
      <div className="wv-detail__head">
        <h3 className="wv-detail__title">{name.title}</h3>
        <Badge tone={badge.tone}>{badge.text}</Badge>
        <span className="wv-card__win">
          {windowLabel(work.windowFrom, work.windowTo)}
          {name.note ? ` · ${name.note}` : ''}
        </span>
        <Link className="wv-detail__more" to={`/analysis/water${linkSearch({ cycle, work: workKey(work) })}`}>
          {t(m.detailMore)}
        </Link>
      </div>

      <div className="wstats wv-stats">
        {events ? (
          <>
            <div className="wstat">
              <span className="wstat__key">{t(m.statDelivered)}</span>
              <span className="wstat__val">{m3Compact(b.deliveredM3)}</span>
              <span className="wstat__note">{t(m.statDeliveredEventsNote, { deliveries: countOf(b.deliveries, m.deliveries) })}</span>
            </div>
            <div className="wstat">
              <span className="wstat__key">{t(m.statPairsDone)}</span>
              <span className="wstat__val">{num(b.obligationsClosed)}</span>
              <span className="wstat__note">{t(m.statPairsDoneNote, { total: t(m.pairsWord, { count: work.obligations }), area: haUnit(work.areaHa) })}</span>
            </div>
            <div className="wstat">
              <span className="wstat__key">{state === 'done' ? t(m.statNoDelivery) : t(m.statWaiting)}</span>
              <span className="wstat__val">{num(b.obligationsOpen)}</span>
              <span className="wstat__note">{state === 'done' ? t(m.statNoDeliveryNote) : t(m.statWaitingNote)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="wstat">
              <span className="wstat__key">{t(m.statRequested)}</span>
              <span className="wstat__val">{m3Compact(b.requestedM3)}</span>
              <span className="wstat__note">{countOf(b.requests, m.requests)}</span>
            </div>
            <div className="wstat">
              <span className="wstat__key">{t(m.statDelivered)}</span>
              <span className="wstat__val">{m3Compact(b.deliveredM3)}</span>
              <span className="wstat__note">{t(m.statDeliveredNote, { coverage: pct(ratio, 1), deliveries: countOf(b.deliveries, m.deliveries) })}</span>
            </div>
            <div className="wstat">
              <span className="wstat__key">{t(m.statShort)}</span>
              <span className="wstat__val">{!gap || gap.underM3 === null ? t(m.noData) : m3Compact(gap.underM3)}</span>
              <span className="wstat__note">{t(m.statShortNote, { hasOver: gap?.overM3 ? 'yes' : 'no', over: gap?.overM3 ? m3Compact(gap.overM3) : '' })}</span>
            </div>
            <div className="wstat">
              <span className="wstat__key">{t(m.statWorks)}</span>
              <span className="wstat__val">{num(work.obligations)}</span>
              <span className="wstat__note">{haUnit(work.areaHa)}</span>
            </div>
          </>
        )}
        <div className="wstat">
          <span className="wstat__key">{t(m.statUnplanned)}</span>
          <span className="wstat__val">{num(b.obligationsUnplanned)}</span>
          <span className="wstat__note">{t(m.statUnplannedNote)}</span>
        </div>
      </div>

      {channelRow ? (
        <div>
          <ChannelChip label={channelRow.label} onClear={() => setChannel(null)} />
        </div>
      ) : null}

      <AgroMap
        colorBy="water"
        mode="frame"
        height={MAP_HEIGHT}
        legend="collapsed"
        data={{ waterById }}
        filterIds={contourIds.length > 0 ? contourIds : undefined}
        focusIds={focusRow ? focusRow.contourIds : contourIds.length > 0 ? contourIds : undefined}
        timeline={timeline}
        layerUnavailable={contourIds.length === 0 ? view.emptyNote || t(m.noContourDistribution) : null}
        caption={t(m.mapCaption, { watering: name.title })}
        panel={mapPanel}
        cardExtra={(c) => {
          const row = byContour.get(c.id);
          if (!row) return null;
          return (
            <dl className="wv-cardx">
              <dt>{t(m.cardThisWatering)}</dt>
              <dd>
                {events
                  ? t(m.cardVolumesEvents, { delivered: volume(row.deliveredM3) })
                  : t(m.cardVolumes, { requested: volume(row.requestedM3), delivered: volume(row.deliveredM3) })}
              </dd>
              {row.actorName ? (
                <>
                  <dt>{events ? t(m.cardFarm) : t(m.cardRequest)}</dt>
                  <dd>{row.actorName}</dd>
                </>
              ) : null}
            </dl>
          );
        }}
      />
      <p className="t-caption wv-note">
        <EvidenceMark kind={work.demo ? 'demo' : 'fact'} />
        <InfoPopover icon="info" label={t(m.mapInfoLabel)}>
          {t(m.mapInfoBody, { where })}
        </InfoPopover>
      </p>

      <div className="wv-section">
        <h4 className="wv-h">
          {t(m.farmsOfWatering)}
          {focusRow ? (
            <button type="button" className="wv-chip focus-ring" onClick={() => setFocusFarm(null)}>
              {t(m.onMapChip, { name: focusRow.name })}
            </button>
          ) : null}
        </h4>
        <DataTable
          columns={farmColumns((r) => setFocusFarm((v) => (v === r.actorId ? null : r.actorId)), focusFarm, events)}
          rows={farms}
          getKey={(r) => String(r.actorId)}
          denominator={m.farmsDenominator}
          empty={{ title: t(m.noFarmsTitle), reason: view.emptyNote || (events ? t(m.noFarmsReasonEvents) : t(m.noFarmsReason)) }}
        />
      </div>

      <WateringChannels channels={channels} channel={channel} setChannel={setChannel} events={events} />
    </div>
  );
}

function WateringChannels({
  channels,
  channel,
  setChannel,
  events,
}: {
  channels: ChannelRow[];
  channel: string | null;
  setChannel: (key: string | null) => void;
  events: boolean;
}) {
  const t = useT();
  return (
    <div className="wv-section">
      <h4 className="wv-h">{t(m.channelsTitle)}</h4>
      <ChannelList rows={channels} selected={channel} onSelect={setChannel} events={events} />
    </div>
  );
}

/* ---------------------------------------------------- сверка и журнал */

type MismatchRow = CycleWaterView['mismatches'][number] & { watering: string };

const mismatchColumns = (): Column<MismatchRow>[] => [
  { key: 'watering', header: tr(m.colWatering), render: (d) => d.watering },
  { key: 'channel', header: tr(m.colChannel), render: (d) => d.channel || tr(m.channelUnnamed) },
  { key: 'period', header: tr(m.colPeriod), render: (d) => windowLabel(d.periodFrom, d.periodTo) },
  { key: 'volume', header: tr(m.colVolume), unit: tr(m.unitM3), numeric: true, render: (d) => (d.totalM3 === null ? tr(m.noData) : num(d.totalM3)) },
  { key: 'diff', header: tr(m.colDiff), unit: tr(m.unitM3), numeric: true, render: (d) => (d.differenceM3 === null ? tr(m.noData) : num(d.differenceM3)) },
  { key: 'rec', header: tr(m.colRecon), render: (d) => <span title={d.reason || undefined}>{tr(RECONCILIATION_WORDS.mismatched)}</span> },
];

/** Расхождения сверки — список сервера, уже отсортированный по размеру разницы. */
function Mismatches({ water, works }: { water: CycleWaterView; works: IrrigationWork[] }) {
  const titles = new Map(works.map((w) => [workKey(w), wateringTitle(w.name).title]));
  const rows = water.mismatches.map((row) => ({
    ...row,
    watering: titles.get(`${row.campaignId}:${row.workDefId}`) ?? tr(m.wateringFallback),
  }));
  return (
    <DataTable
      columns={mismatchColumns()}
      rows={rows}
      getKey={(d) => `${d.campaignId}-${d.deliveryId}`}
      denominator={m.deliveriesDenominator}
      empty={{ title: tr(m.noMismatchTitle), reason: tr(m.noMismatchReason) }}
    />
  );
}

const ledgerColumns = (): Column<LedgerRow>[] => [
  { key: 'farm', header: tr(m.colRequestFarm), render: (r) => <Link to={`/farms/${r.actorId}`}>{r.actorName ?? tr(m.nameUnknown)}</Link> },
  { key: 'submitted', header: tr(m.colSubmitted), render: (r) => dateShort(r.submittedOn) },
  {
    key: 'desired',
    header: tr(m.colDesired),
    render: (r) => (r.desiredFrom || r.desiredTo ? windowLabel(r.desiredFrom, r.desiredTo) : tr(m.desiredNone)),
  },
  { key: 'contours', header: tr(m.colContours), numeric: true, render: (r) => num(r.contours) },
  { key: 'req', header: tr(m.colRequested), unit: tr(m.unitM3), numeric: true, render: (r) => (r.requestedM3 === null ? tr(m.noData) : num(r.requestedM3)) },
  { key: 'del', header: tr(m.colDelivered), unit: tr(m.unitM3), numeric: true, render: (r) => (r.deliveredM3 === null ? tr(m.noData) : num(r.deliveredM3)) },
];

/** Раскладка экрана «Вода» в загрузке: KPI, карточка поливов (лента + деталь), разрез по хозяйствам. */
function WaterBodySkeleton() {
  const t = useT();
  return (
    <SkeletonRegion label={t(m.skelBodyLabel)}>
      <Stack>
        <Grid cols={4}>
          {[0, 1, 2, 3].map((i) => <SkeletonKpi key={i} quiet />)}
        </Grid>
        <SkeletonCard quiet size="lg" title={t(m.skelWaterings)}>
          <div className="wv-split">
            <div className="wv-split__rail"><SkeletonList quiet items={5} tone="soft" /></div>
            <div className="wv-split__main"><WateringDetailSkeleton quiet /></div>
          </div>
        </SkeletonCard>
        <SkeletonChart quiet title={t(m.skelFarmsChart)} height={200} bars={8} />
      </Stack>
    </SkeletonRegion>
  );
}

/** Деталь полива: карта и график каналов. */
function WateringDetailSkeleton({ quiet }: { quiet?: boolean }) {
  const t = useT();
  return (
    <Stack tight>
      <SkeletonMap quiet={quiet} height={360} />
      <SkeletonChart quiet={quiet} title={t(m.skelChannels)} height={140} bars={6} />
    </Stack>
  );
}

function RequestLedger({ params }: { params: QueryParams }) {
  const t = useT();
  const [offset, setOffset] = useState(0);
  const key = paramsKey(params);
  // Смена среза — новая выборка: листать её с середины прежней нельзя.
  useEffect(() => setOffset(0), [key]);
  const res = useRequest(() => api.waterLedger({ ...params, limit: LEDGER_PAGE, offset }), [key, offset]);
  return (
    <RequestGate res={res} what={t(m.ledgerLoadWhat)} skeleton={<SkeletonTable label={t(m.ledgerLoadWhat)} rows={6} cols={5} />}>
      {(reply) => {
        const view = toLedger(reply);
        if (view.rows.length === 0) {
          return <EmptyState compact title={t(m.ledgerEmptyTitle)} reason={view.emptyNote || view.balance.outcomeReason} />;
        }
        const { limit, offset: at, total } = view.page;
        return (
          <Stack tight>
            <DataTable columns={ledgerColumns()} rows={view.rows} getKey={(r) => String(r.requestId)} limit={limit} denominator={m.requestsDenominator} />
            <div className="chips" style={{ alignItems: 'center' }}>
              <button type="button" className="btn btn--secondary btn--sm" disabled={at === 0} onClick={() => setOffset(Math.max(0, at - limit))}>
                {t(m.prev)}
              </button>
              <span className="t-caption">
                {t(m.pageRange, { from: num(at + 1), to: num(Math.min(at + limit, total)), total: num(total) })}
              </span>
              <button type="button" className="btn btn--secondary btn--sm" disabled={at + limit >= total} onClick={() => setOffset(at + limit)}>
                {t(m.next)}
              </button>
            </div>
          </Stack>
        );
      }}
    </RequestGate>
  );
}

/* --------------------------------------------------------------- экран */

export function WaterCanon() {
  const t = useT();
  const ctx = useAnalysisContext();
  const { viewParams, params, asOf, linkSearch, view, setViewFilter } = ctx;
  const choice = useIrrigationChoice(viewParams);
  const cycleWater = useCycleWater(viewParams, choice.works.length > 0 ? choice.cycle?.cycle ?? null : null);
  const workWater = useWorkWater(viewParams, choice.work);
  // Вода района по файлу ирригации — один запрос на блок и KPI агроцикла.
  const district = useWaterDistrict(choice.cycle?.cycle ?? null, choice.cycles.data !== null);
  const review = useRequest(() => api.waterReview(viewParams), [paramsKey(viewParams)]);
  const massifs = useRequest(() => api.massifs(params), [paramsKey(params)]);
  const [search, setSearch] = useSearchParams();
  const channel = search.get('channel');
  const setChannel = (key: string | null) =>
    setSearch(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === null) next.delete('channel');
        else next.set('channel', key);
        return next;
      },
      { replace: true },
    );

  usePageTrail([
    { level: t(m.trailDistrict), name: t(m.trailDistrictName), to: '/' },
    { level: t(m.trailSection), name: t(m.pageTitle) },
  ]);

  const cycleName = choice.cycle?.name ?? t(m.cycleFallback);
  const data = cycleWater.data ?? null;
  // Заявок нет ни в картине сезона, ни в воде цикла — вода только по событиям файла
  // ирригации: «запрошено», недоподача и сверка распределений не показываются.
  const hasRequests =
    (choice.share?.requestedM3 ?? null) !== null || (data ? [...data.works.values()].some((w) => w.requestedM3 !== null) : false);
  const events = choice.share !== null && !hasRequests;
  const reviewCount = review.data ? review.data.items.length : null;
  const selected = workWater.data ?? null;

  // Разрез хозяйств цикла и поливы вне плана — готовые поля ответа сервера.
  const cycleFarms = data?.farms ?? [];
  const unplanned = data ? choice.works.filter((w) => (data.works.get(workKey(w))?.unplanned ?? 0) > 0) : [];

  const massifOptions = [
    { id: 'all', name: t(m.allMassifs) },
    ...[...(massifs.data?.items ?? [])]
      .map((m) => ({ id: String(m.id), name: m.canonical_name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  ];

  return (
    <Stack>
      <PageHeader
        level="district"
        title={t(m.pageTitle)}
        purpose={t(m.pagePurpose)}
        actions={
          <div className="wv-head" data-guide="filters">
            {/* Агроцикл и массив — справа напротив заголовка, как на «Обзоре района». */}
            <div className="wv-controls">
              {choice.choices.length > 0 ? (
                <ChoiceSelect
                  label={t(m.cycleLabel)}
                  value={choice.cycle?.cycle ?? ''}
                  options={choice.choices.map((c) => ({ id: c.cycle, name: c.name, description: c.status === 'active' ? t(m.currentCycle) : undefined }))}
                  onChange={choice.setCycle}
                />
              ) : choice.cycles.state === 'loading' ? (
                // Поле цикла стоит на месте, пока список циклов идёт: «Массив» не съезжает.
                <ChoiceSelect label={t(m.cycleLabel)} value="" options={[{ id: '', name: '…' }]} onChange={() => {}} disabled />
              ) : null}
              <ChoiceSelect
                label={t(m.massifLabel)}
                value={view.massifId === null ? 'all' : String(view.massifId)}
                options={massifOptions}
                onChange={(v) => setViewFilter('massifId', v === 'all' ? null : Number(v))}
              />
            </div>
            <span className="wv-actions">
              {choice.ref ? <EvidenceMark kind={asOf ? 'derived' : 'fact'} label={t(m.stateOn, { date: dateShort(choice.ref) })} /> : null}
              <Link className="linkish" to={`/analysis/water${linkSearch()}`}>
                {t(m.moreWater)}
              </Link>
            </span>
          </div>
        }
      >
        {choice.works.length > 0 ? (
          <Lead works={choice.works} day={choice.ref} share={choice.share} cycleName={cycleName} />
        ) : choice.cycles.state === 'loading' || (choice.choices.length > 0 && choice.picture.state === 'loading') ? (
          // Строка «полив идёт / не идёт» до картины — две строки в её месте (6al.49).
          <div className="wlead" aria-hidden="true">
            <Skeleton variant="circle" width={18} />
            <span className="wlead__text" style={{ display: 'grid', gap: 'var(--sp-2)', width: 'min(750px, 100%)' }}>
              <Skeleton width="100%" />
              <Skeleton width="72%" />
            </span>
          </div>
        ) : null}
      </PageHeader>

      <RequestGate res={choice.cycles} what={t(m.cyclesLoadWhat)} skeleton={<WaterBodySkeleton />}>
        {() =>
          choice.choices.length === 0 ? (
            <EmptyState compact title={t(m.noCyclesTitle)} reason={choice.cyclesEmptyNote ?? t(m.noCyclesReason)} />
          ) : (
            <RequestGate res={choice.picture} what={t(m.wateringsLoadWhat)} skeleton={<WaterBodySkeleton />}>
              {() =>
                choice.works.length === 0 ? (
                  <EmptyState
                    compact
                    icon={Droplets}
                    title={t(m.noWateringsTitle)}
                    reason={choice.waterNote ?? t(m.noWateringsReason)}
                  />
                ) : (
                  <Stack>
                    <div data-guide="kpi">
                      <WaterKpis
                        works={choice.works}
                        day={choice.ref}
                        water={data}
                        share={choice.share}
                        cycleName={cycleName}
                        events={events}
                        district={district.data}
                        review={reviewCount}
                      />
                    </div>

                    <Card
                      size="lg"
                      title={t(m.skelWaterings)}
                      aside={<Badge tone="neutral">{countOf(choice.works.length, m.waterings)}</Badge>}
                    >
                      <div className="wv-split">
                        <div className="wv-split__rail" data-guide="feed">
                          {/* Вкладок циклов над лентой больше нет (К3, 07.10.2026):
                              тот же выбор уже стоит полем «Цикл» в шапке, и два
                              переключателя одной оси спорили бы друг с другом. */}
                          <WateringFeed
                            works={choice.works}
                            day={choice.ref}
                            selected={choice.work}
                            water={data}
                            onPick={choice.setWork}
                            events={events}
                          />
                        </div>
                        <div className="wv-split__main">
                          <RequestGate res={workWater} what={t(m.deliveriesLoadWhat)} skeleton={<WateringDetailSkeleton />}>
                            {() =>
                              selected ? (
                                <WateringDetail
                                  item={selected}
                                  day={choice.ref}
                                  channel={channel}
                                  setChannel={setChannel}
                                  linkSearch={linkSearch}
                                  cycle={choice.cycle!.cycle}
                                  events={events}
                                  mapPanel={{
                                    title: t(m.skelWaterings),
                                    summary: wateringTitle(selected.work.name).title,
                                    content: (
                                      <WateringFeed
                                        works={choice.works}
                                        day={choice.ref}
                                        selected={choice.work}
                                        water={data}
                                        onPick={choice.setWork}
                                        events={events}
                                        compact
                                      />
                                    ),
                                  }}
                                />
                              ) : (
                                <EmptyState compact title={t(m.noWateringPickedTitle)} reason={t(m.noWateringPickedReason)} />
                              )
                            }
                          </RequestGate>
                        </div>
                      </div>
                    </Card>

                    {events ? null : !data ? (
                      <SkeletonChart label={t(m.farmsChartLabel)} title={t(m.skelFarmsChart)} height={200} bars={8} />
                    ) : (
                      <Card
                        title={t(m.skelFarmsChart)}
                        aside={<span className="t-caption">{t(m.farmsChartAside, { farms: countOf(cycleFarms.length, WORDS.farm) })}</span>}
                      >
                        <div data-guide="shortfall">
                          <FarmDiverging rows={cycleFarms} shortfall={data.shortfall} />
                        </div>
                      </Card>
                    )}

                    {/* «Где разобраться» — после рабочего блока: топ хозяйств по земле без подачи (журнал №1450). */}
                    <WaterWhereToLook res={district} allTo={`/analysis/water${linkSearch({ cycle: choice.cycle!.cycle })}`} />

                    {/* Справка — свёрнута по умолчанию: каналы, вода вне плана, сверка и журнал. */}
                    <div data-guide="reference">
                    <Disclosure
                      label={events ? t(m.refTitle) : t(m.refTitleRequests)}
                      summary={
                        district.data && data
                          ? events
                            ? districtReferenceSummary(district.data)
                            : t(m.refSummaryRequests, {
                                district: districtReferenceSummary(district.data),
                                reconcile: t(m.mismatchesAndUnplanned, { mismatched: data.mismatched, unplanned: num(data.unplanned) }),
                              })
                          : <Skeleton width={200} />
                      }
                    >
                      <Stack>
                        {district.data ? (
                          <WaterDistrictReference d={district.data} cycle={choice.cycle!.cycle} allCanalsTo={`/analysis/water${linkSearch({ cycle: choice.cycle!.cycle })}`} />
                        ) : null}
                        {events ? null : (
                          <div className="wv-section" data-guide="mismatches">
                            <h4 className="wv-h">{t(m.mismatchesTitle)}</h4>
                            {data ? <Mismatches water={data} works={choice.works} /> : <SkeletonTable label={t(m.mismatchesLabel)} rows={3} cols={4} />}
                          </div>
                        )}
                        {/* При воде по файлу «вне плана» района уже названо справкой выше
                            (события файла); пустой список подач по поливам рядом спорил бы с ней. */}
                        {events && unplanned.length === 0 ? null : (
                        <div className="wv-section">
                          <h4 className="wv-h">{t(m.unplannedTitle)}</h4>
                          {unplanned.length === 0 ? (
                            <p className="t-caption">{t(m.unplannedNone)}</p>
                          ) : (
                            <ul className="wv-plain">
                              {unplanned.map((w) => (
                                <li key={workKey(w)}>
                                  {t(m.unplannedItem, { watering: wateringTitle(w.name).title, count: num(data?.works.get(workKey(w))?.unplanned ?? 0) })}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        )}
                        {events ? null : (
                          <div className="wv-section" data-guide="journal">
                            <h4 className="wv-h">{t(m.journalTitle)}</h4>
                            <RequestLedger params={viewParams} />
                          </div>
                        )}
                        {choice.waterNote && !events ? <p className="t-caption">{choice.waterNote}</p> : null}
                      </Stack>
                    </Disclosure>
                    </div>
                  </Stack>
                )
              }
            </RequestGate>
          )
        }
      </RequestGate>
    </Stack>
  );
}
